import { FC } from 'react';
import { useEventGeneratorClickCta } from 'src/analytics/useEventGeneratorClickCta';
import { Button } from 'src/components';
import { useCtaHref } from 'src/hooks/useCtaHref';
import Card from 'src/features/PussInBoots/PussInBootsTopicOfTopicPageContent/LearnCommunicate/Card';
import Learnables from 'src/features/PussInBoots/PussInBootsTopicOfTopicPageContent/Learnables';
import LearnableCard from 'src/features/PussInBoots/PussInBootsTopicOfTopicPageContent/LearnableCard';
import { useIntl } from 'react-intl';
import { YoutubeScenario } from 'src/types/domain';
import { useTargetMessages } from 'src/hooks/useTargetMessages';
import * as S from '../styles';
import { Section, SectionContent, YoutubeIframe } from './styles';
import * as msgsData from './messages';

const { msgs, ...localesWithMessages } = msgsData;

type LearnProps = { scenario: YoutubeScenario };

const PALETTE = 'red';

const Learn: FC<LearnProps> = ({ scenario: { learnables, mediaMetadata } }) => {
  const intl = useIntl();
  const targetMsgs = useTargetMessages(localesWithMessages);

  const signUpHref = useCtaHref();
  const eventGeneratorClickCta = useEventGeneratorClickCta();

  return (
    <Section>
      <SectionContent>
        <S.SubTitle>{intl.formatMessage(msgs.subtitle)}</S.SubTitle>
        <S.Title>{intl.formatMessage(targetMsgs.title)}</S.Title>
        <S.Description>{intl.formatMessage(targetMsgs.description)}</S.Description>

        <Card $palette={PALETTE}>
          <S.TopContainer>
            <S.LeftSideContainer>
              <S.LeftSideSubTitle>
                {intl.formatMessage(msgs.pibLearn)}
                <S.Icon name="starsSet" />
              </S.LeftSideSubTitle>
              <S.LeftSideTitle>
                {intl.formatMessage(targetMsgs.leftSectionTitle, {
                  amount: learnables.length,
                })}
              </S.LeftSideTitle>
              <S.LeftSideDescription>
                {intl.formatMessage(targetMsgs.topSectionDescription)}
              </S.LeftSideDescription>
              <S.LeftSideLearnables>
                {learnables
                  .filter(l => l.videoUrls.length)
                  .slice(0, 3)
                  .map(learnable => (
                    <LearnableCard
                      $palette={PALETTE}
                      key={learnable.learnableId}
                      learnable={learnable}
                    />
                  ))}
              </S.LeftSideLearnables>
            </S.LeftSideContainer>

            <S.RightSideContainer>
              <S.RightSideLearnables>
                <Learnables learnables={learnables} $palette={PALETTE} />
              </S.RightSideLearnables>

              <S.RightSideCTA>
                <Button
                  analyticsEvent={eventGeneratorClickCta}
                  buttonSize="large"
                  buttonType="primary"
                  fullWidth="always"
                  href={signUpHref}
                >
                  {intl.formatMessage(msgs.vocabularyCta)}
                </Button>
              </S.RightSideCTA>
            </S.RightSideContainer>
          </S.TopContainer>

          <S.BottomContainer>
            <S.LeftSideContainer>
              <YoutubeIframe url={mediaMetadata.youtube.url} />
            </S.LeftSideContainer>
            <S.RightSideContainer>
              <S.LeftSideSubTitle>
                {intl.formatMessage(msgs.pibImmerse)}
                <S.Icon name="starsSet" />
              </S.LeftSideSubTitle>
              <S.LeftSideTitle>{intl.formatMessage(msgs.bottomSectionTitle)}</S.LeftSideTitle>
              <S.LeftSideDescription>
                {intl.formatMessage(msgs.bottomSectionDescription)}
              </S.LeftSideDescription>
            </S.RightSideContainer>
          </S.BottomContainer>
        </Card>

        <S.CTA>
          <Button
            analyticsEvent={eventGeneratorClickCta}
            buttonSize="large"
            buttonType="primary"
            disableMinWidth
            fullWidth="never"
            href={signUpHref}
          >
            {intl.formatMessage(msgs.learningCta)}
          </Button>
        </S.CTA>
      </SectionContent>
    </Section>
  );
};

export default Learn;
